import axios from 'axios';

const publicApi = axios.create({
  baseURL: '/api/public',
});

export interface ResendRequestData {
  id: string;
  status: string;
  expiresAt: string;
  customerEmail: string;
  customerName: string | null;
  shippingAddress: Record<string, string>;
  shippingMethod: string | null;
  paczkomatCode: string | null;
  amount: number;
  currency: string;
  paidAt: string | null;
  resendOrderId: string | null;
  paymentRetryAt: string | null;
  paymentItem: {
    name: string;
    price: number;
  };
  order: {
    orderNumber: string;
    items: Array<{
      name: string;
      quantity: number;
      price: string;
      total: string;
      sku?: string;
    }>;
    shippingMethod: string | null;
    customerName: string | null;
    customerPhone: string | null;
  };
  branding: {
    storeName: string;
    logoUrl: string | null;
    logoMaxWidth?: number;
    logoMaxWidthMobile?: number;
    backgroundColor: string;
    accentColor: string;
    fontColor: string;
    buttonFontColor: string;
    cardColor: string;
    separatorColor: string;
    buttonColor: string;
    buttonHoverColor: string;
  };
}

export const resendPublicApi = {
  getResendRequest: (token: string) =>
    publicApi.get<ResendRequestData>(`/resend/${token}`).then(r => r.data),

  updateAddress: (token: string, data: { shippingAddress: Record<string, string>; shippingMethod?: string }) =>
    publicApi.patch<{
      success: boolean;
      shippingAddress: Record<string, string>;
      shippingMethod: string | null;
    }>(`/resend/${token}`, data).then(r => r.data),

  initiatePayment: (token: string) =>
    publicApi.post<{ redirectUrl: string }>(`/resend/${token}/pay`).then(r => r.data),
};

// ============================================================================
// Portal (Customer Panel) API
// ============================================================================

export interface PortalOrderSummary {
  id: string;
  orderNumber: string;
  status: string;
  statusColor: string;
  totalAmount: number;
  currency: string;
  createdAt: string;
  source: string;
  shippingMethod: string | null;
  itemCount: number;
  complaintsAllowed: boolean;
  latestShipment: {
    id: string;
    trackingNumber: string | null;
    carrier: string;
    status: string;
  } | null;
}

export interface PortalData {
  customerEmail: string;
  customerName: string | null;
  branding: {
    storeName: string;
    storeEmail: string | null;
    logoUrl: string | null;
    backgroundColor: string;
    accentColor: string;
    fontColor: string;
    buttonFontColor: string;
    cardColor?: string;
    separatorColor?: string;
    buttonColor?: string;
    buttonHoverColor?: string;
    logoMaxWidth?: number;
    logoMaxWidthMobile?: number;
  };
  features: {
    complaintsEnabled: boolean;
    invoicesEnabled: boolean;
    resendEnabled: boolean;
    addressEditEnabled: boolean;
  };
  orders: PortalOrderSummary[];
}

export interface PortalOrderDetail {
  id: string;
  orderNumber: string;
  status: string;
  statusColor: string;
  totalAmount: number;
  currency: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  shippingAddress: Record<string, string>;
  billingAddress: Record<string, string>;
  items: Array<{
    name: string;
    quantity: number;
    price?: string;
    total: string;
    sku?: string;
  }>;
  shippingMethod: string | null;
  paymentMethod: string | null;
  isCOD: boolean;
  source: string;
  createdAt: string;
  shipments: Array<{
    id: string;
    trackingNumber: string | null;
    carrier: string;
    status: string;
    createdAt: string;
    deliveredAt: string | null;
    trackingUrl: string | null;
  }>;
  history: Array<{
    id: string;
    action: string;
    description: string;
    createdAt: string;
  }>;
}

export interface PortalInvoice {
  id: string;
  invoiceNumber: string | null;
  type: string;
  status: string;
  issueDate: string | null;
  grossAmount: number | null;
  currency: string;
  hasPdf: boolean;
}

export interface ComplaintAttachment {
  filename: string;
  originalName: string;
  url: string;
  size: number;
  mimeType: string;
  deleted?: boolean;
  deletedReason?: string;
}

export interface PortalComplaint {
  id: string;
  type: string;
  reason: string;
  items: Array<{ name: string; quantity: number }>;
  metadata: Record<string, unknown>;
  attachments: ComplaintAttachment[];
  bankAccountNumber: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
  resolvedAt: string | null;
}

export const portalPublicApi = {
  getPortal: (token: string) =>
    publicApi.get<PortalData>(`/portal/${token}`).then(r => r.data),

  getOrder: (token: string, orderId: string) =>
    publicApi.get<PortalOrderDetail>(`/portal/${token}/orders/${orderId}`).then(r => r.data),

  updateAddress: (token: string, orderId: string, data: { shippingAddress: Record<string, string> }) =>
    publicApi.patch(`/portal/${token}/orders/${orderId}/address`, data).then(r => r.data),

  initiateResend: (token: string, orderId: string) =>
    publicApi.post<{ resendRequestId: string; resendToken: string; resendLink: string; expiresAt: string }>(
      `/portal/${token}/orders/${orderId}/resend`
    ).then(r => r.data),

  getInvoices: (token: string, orderId: string) =>
    publicApi.get<PortalInvoice[]>(`/portal/${token}/orders/${orderId}/invoices`).then(r => r.data),

  downloadInvoicePdf: (token: string, orderId: string, invoiceId: string) =>
    publicApi.get(`/portal/${token}/orders/${orderId}/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    }).then(r => r.data),

  getShipments: (token: string, orderId: string) =>
    publicApi.get(`/portal/${token}/orders/${orderId}/shipments`).then(r => r.data),

  uploadAttachments: (token: string, orderId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    return publicApi.post<{ attachments: ComplaintAttachment[] }>(
      `/portal/${token}/orders/${orderId}/complaint/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    ).then(r => r.data);
  },

  submitComplaint: (token: string, orderId: string, data: {
    type: string;
    reason: string;
    items?: Array<{ name: string; quantity: number; sku?: string }>;
    metadata?: Record<string, unknown>;
    bankAccountNumber?: string | null;
    attachments?: ComplaintAttachment[];
  }) =>
    publicApi.post<PortalComplaint>(`/portal/${token}/orders/${orderId}/complaint`, data).then(r => r.data),

  getComplaints: (token: string, orderId: string) =>
    publicApi.get<PortalComplaint[]>(`/portal/${token}/orders/${orderId}/complaints`).then(r => r.data),
};
